import React, { type FunctionComponent, useCallback } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { Select } from '@retailer-platform/shared-components'
import { getRepeatTypeFromDaysOfWeek } from 'domains/shifts/components/utils/shiftsUtils'
import { useShiftDetailsFormStyles } from 'domains/shifts/components/contexts/ShiftDetailsFormStylesContext'
import { FormRow, StyledDropdownContainer, StyledLabel } from './ShiftDetailsForm.styles'
import {
  type PartnerPickRecurringShiftsFormData,
  RepeatShiftEnum,
  type RepeatShiftOption,
} from './recurringShift.types'
import { usePartnerPickRecurringShiftFormOptions } from './usePartnerPickRecurringShiftFormOptions'

interface ShiftRepeatDropDownProps {
  register: FormContextValues<PartnerPickRecurringShiftsFormData>['register']
  control: FormContextValues<PartnerPickRecurringShiftsFormData>['control']
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
  watch: FormContextValues<PartnerPickRecurringShiftsFormData>['watch']
  label: string
  date: string
  defaultValue?: RepeatShiftEnum
  recurShiftsToBeNonRecur: number[]
  recurringShiftId: number
}

export const ShiftRepeatDropDown: FunctionComponent<ShiftRepeatDropDownProps> = ({
  date,
  setValue,
  watch,
  label,
  recurShiftsToBeNonRecur,
  recurringShiftId,
}) => {
  const { repeatShiftOptions, getDaysOfWeekFromRepeatType } =
    usePartnerPickRecurringShiftFormOptions(date)
  const { daysOfWeek, repeatShift } = watch(['daysOfWeek', 'repeatShift'])

  const getDefaultValue = (): RepeatShiftOption => {
    if (daysOfWeek) {
      const repeatType = recurShiftsToBeNonRecur.includes(recurringShiftId)
        ? RepeatShiftEnum.DoesNotRepeat
        : getRepeatTypeFromDaysOfWeek(daysOfWeek)
      return repeatShiftOptions.filter(option => option.value === repeatType)[0]
    }
    return repeatShiftOptions.filter(option => option.value === RepeatShiftEnum.DoesNotRepeat)[0]
  }

  const defaultValue = getDefaultValue()

  const onChange = useCallback(
    (data: { label: string; value: RepeatShiftEnum }) => {
      const daysOfWeek = getDaysOfWeekFromRepeatType(data.value, date)
      setValue('repeatShift', data, true)
      setValue('daysOfWeek', daysOfWeek, true)
    },
    [setValue, date, getDaysOfWeekFromRepeatType]
  )

  const styles = useShiftDetailsFormStyles()

  return (
    <FormRow>
      <StyledDropdownContainer>
        <StyledLabel> {label} </StyledLabel>
        <Select
          data-testid="shopper-shift-form-repeat-type-select"
          name="repeatShift"
          options={repeatShiftOptions}
          value={repeatShift}
          onChange={onChange}
          defaultValue={defaultValue}
          styles={styles}
        />
      </StyledDropdownContainer>
    </FormRow>
  )
}
